/**
 * VideoVipTemplate — Fullscreen Mux video with admin-configurable CTA mode.
 *
 * Mode driven by /api/cta-resolved (ctas.video.mode):
 *   sin_boton  — no button (default when nothing is configured)
 *   exclusivo  — "Acceso exclusivo" → /invitacion-premium
 *   comunidad  — "Unirte a la comunidad" → broadcast_href (telegram_basic category)
 *   wishlist   — "Lista de espera" → menu_wishlist_url (vip_membership category)
 *   video_vip  — "Ser Miembro" → /invitacion-miembro (DualCTA checkout landing)
 *
 * Playback ID: ctas.video.playback_id → VITE_VIDEO_VIP_PLAYBACK_ID → VITE_PREMIUM_MUX_ES_VIP.
 *
 * Route: /video-vip
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import { useResolvedCtas, resolveField } from "@/hooks/useResolvedCtas";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import {
  applyInstagramBrowserCSS,
  isInstagramBrowser,
} from "@/lib/instagramBrowserDetection";
import { trackTemplateCta, trackTemplateView } from "@/lib/templateCtaAnalytics";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

const BUILD_TIME_PLAYBACK_ID =
  (import.meta.env.VITE_VIDEO_VIP_PLAYBACK_ID as string | undefined)?.trim() ||
  (import.meta.env.VITE_PREMIUM_MUX_ES_VIP as string | undefined)?.trim() ||
  "";

type VideoVipMode =
  | "sin_boton"
  | "exclusivo"
  | "comunidad"
  | "wishlist"
  | "video_vip";

const VALID_MODES = new Set<VideoVipMode>([
  "sin_boton",
  "exclusivo",
  "comunidad",
  "wishlist",
  "video_vip",
]);

interface CtaConfig {
  label: string;
  href: string;
  external?: boolean;
}

function buildCta(
  mode: VideoVipMode,
  broadcastHref: string,
  wishlistUrl: string,
): CtaConfig | null {
  switch (mode) {
    case "exclusivo":
      return { label: "Acceso exclusivo", href: "/invitacion-premium" };
    case "comunidad":
      return broadcastHref
        ? { label: "Unirte a la comunidad", href: broadcastHref, external: true }
        : null;
    case "wishlist":
      return wishlistUrl
        ? {
            label: "Lista de espera",
            href: wishlistUrl,
            external: wishlistUrl.startsWith("http"),
          }
        : null;
    case "video_vip":
      return { label: "Ser Miembro", href: "/invitacion-miembro" };
    default:
      return null;
  }
}

export default function VideoVipTemplate() {
  const { ctas, ready } = useResolvedCtas();
  const { setTemplateName } = useCurrentTemplate();

  const [videoReady, setVideoReady] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [posterReady, setPosterReady] = useState(false);

  const rawMode = resolveField<string>(ctas.video, "mode", "sin_boton");
  const resolvedPlaybackId = resolveField<string>(
    ctas.video,
    "playback_id",
    BUILD_TIME_PLAYBACK_ID,
  );
  const broadcastHref = resolveField<string>(
    ctas.telegram_basic,
    "broadcast_href",
    "",
  );
  const wishlistUrl = resolveField<string>(
    ctas.vip_membership,
    "menu_wishlist_url",
    "",
  );

  const mode: VideoVipMode = VALID_MODES.has(rawMode as VideoVipMode)
    ? (rawMode as VideoVipMode)
    : "sin_boton";
  const cta = buildCta(mode, broadcastHref, wishlistUrl);

  useEffect(() => {
    setTemplateName(null);
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    const prev = {
      overflow: document.body.style.overflow,
      height: document.body.style.height,
      position: document.body.style.position,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.height = prev.height;
      document.body.style.position = prev.position;
      document.body.style.width = prev.width;
    };
  }, []);

  useEffect(() => {
    if (!isInstagramBrowser()) return;
    applyInstagramBrowserCSS();
  }, []);

  useEffect(() => {
    trackTemplateView("video_vip", "VideoVipTemplate", {
      mode,
      has_cta: !!cta,
      cta_href: cta?.href ?? null,
    });
  }, [mode, cta]);

  return (
    <div
      className="relative w-full h-dvh min-h-dvh overflow-hidden bg-black instagram-browser-modal"
      style={{
        WebkitTransform: "translate3d(0,0,0)",
        touchAction: "none",
        overscrollBehavior: "none",
      }}
    >
      {resolvedPlaybackId && (
        <MuxPosterBackdrop
          playbackId={resolvedPlaybackId}
          fitMode="smartcrop"
          onReady={() => setPosterReady(true)}
        />
      )}

      <MuxVideoPlayerWithCTA
        playbackId={resolvedPlaybackId || undefined}
        forceManualPlaybackId={!!resolvedPlaybackId}
        externalPosterBackdrop={true}
        allowSoundAutoplayInWebView={true}
        variant="fullscreen"
        displayMode="reel"
        conversionReel={true}
        loop={false}
        autoPlay="any"
        skipWorkerTemplateCta={true}
        hideNativeCta={true}
        onReady={() => setVideoReady(true)}
        onPlay={() => {
          setVideoReady(true);
          setVideoPlaying(true);
        }}
        onEnded={() => setVideoEnded(true)}
      />

      {!videoPlaying && !videoEnded && (
        <div
          className="pointer-events-none absolute inset-0 z-[105] transition-opacity duration-500 ease-out"
          role="status"
          aria-live="polite"
          aria-busy={!videoReady}
        >
          {posterReady ? (
            <div
              className="absolute inset-x-0 bottom-0 flex justify-center"
              style={{
                paddingBottom:
                  "max(5.5rem, calc(env(safe-area-inset-bottom, 0px) + 5.5rem))",
              }}
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-black/45 px-3.5 py-2 backdrop-blur-md ring-1 ring-white/10">
                <span className="relative h-3.5 w-3.5">
                  <span className="absolute inset-0 rounded-full border-2 border-white/15" />
                  <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-purple-400 animate-spin" />
                </span>
                <span className="text-[11px] font-medium tracking-wide text-white/80">
                  Cargando…
                </span>
              </span>
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
              <div className="absolute inset-0 bg-black/40" />
              <div className="relative h-12 w-12">
                <div className="absolute inset-0 rounded-full border-2 border-white/15" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-purple-400 animate-spin" />
              </div>
              <span className="relative text-[13px] font-medium uppercase tracking-[0.22em] text-white/70 animate-pulse">
                Cargando
              </span>
            </div>
          )}
        </div>
      )}

      {videoEnded && (
        <div
          className="absolute inset-0 z-[100] bg-black/90 transition-opacity duration-500"
          aria-hidden
        />
      )}

      {ready && cta && (
        <div
          className="absolute inset-0 flex flex-col pointer-events-none"
          style={{ zIndex: 110 }}
        >
          {!videoEnded && (
            <div
              className="absolute bottom-0 left-0 right-0 h-40 [@media(min-height:700px)]:h-52"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.20) 60%, transparent 100%)",
                pointerEvents: "none",
              }}
              aria-hidden
            />
          )}

          <div
            className={cn(
              "relative flex flex-1 justify-center px-5",
              videoEnded ? "items-center" : "items-end",
            )}
            style={
              videoEnded
                ? undefined
                : {
                    paddingBottom:
                      "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))",
                  }
            }
          >
            <div className="pointer-events-auto flex w-full max-w-sm flex-col items-stretch gap-3">
              <a
                href={cta.href}
                {...(cta.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                onClick={() =>
                  trackTemplateCta({
                    prefix: "video_vip",
                    template: "VideoVipTemplate",
                    action: `${mode}_cta`,
                    label: cta.label,
                    destination: cta.href,
                    flowName: "video_vip",
                    extra: {
                      mode,
                      external: cta.external === true,
                    },
                  })
                }
                className={cn(
                  "group flex w-full items-center justify-center gap-1.5 rounded-full",
                  "bg-purple-600/95 hover:bg-purple-500 active:bg-purple-700",
                  "text-white text-sm font-semibold tracking-tight",
                  "px-4 py-3",
                  "shadow-[0_2px_14px_rgba(147,51,234,0.35)] hover:shadow-[0_2px_18px_rgba(147,51,234,0.45)]",
                  "ring-1 ring-purple-400/40",
                  "transition-all duration-150",
                  "no-underline",
                )}
                style={{
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  minHeight: 48,
                }}
              >
                <span className="truncate">{cta.label}</span>
                <span
                  className="text-[11px] opacity-70 transition-transform duration-150 group-hover:translate-x-0.5"
                  aria-hidden
                >
                  →
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
